import * as React from 'react';
// Import props.
import {
	ComparativeDwellingSummaryProps,
	ComparativePerilsDeductiblesSummaryProps,
	FormCoverageComparisonPersonalProps,
	FormProps,
	ScrollbarProps,
} from '@app/props';

interface UseFormCoverageComparisonPersonalParameters {
	id: string;
	props: FormCoverageComparisonPersonalProps,
}

interface UseFormCoverageComparisonPersonalResults {
	applyFormProps: () => FormProps & ScrollbarProps;
	applyComparativeDwellingSummaryProps: () => ComparativeDwellingSummaryProps;
	applyComparativePerilsDeductiblesSummaryProps: () => ComparativePerilsDeductiblesSummaryProps;
}

const useFormCoverageComparisonPersonal = (params: UseFormCoverageComparisonPersonalParameters): UseFormCoverageComparisonPersonalResults => {

	// #region exported methods

	/**
	 * Returns props for form. 
	 **/
	const applyFormProps = (): FormProps & ScrollbarProps => {
		return {
			id: generateIdForForm(),
		};
	};

	/**
	 * Returns props for the comparative buildings summary. 
	 **/
	const applyComparativeDwellingSummaryProps = (): ComparativeDwellingSummaryProps => {
		return {
			id: generateIdForComparativeDwellingSummary(),
			broadLineOfBusinessCode: params.props.broadLineOfBusinessCode,
			propertyDomainValuesProps: params.props.propertyDomainValuesProps,
			comparisonDwellingAddressSummary: params.props.comparisonDwellingAddressSummary,
			comparisonDwellingDetailsSummary: params.props.comparisonDwellingDetailsSummary,
			comparisonDwellingLimitsSummary: params.props.comparisonDwellingLimitsSummary,
			possiblePerils: params.props.possiblePerils,
			possibleStates: params.props.possibleStates,
			focusToErrorOnMount: doesDwellingRequireReview,
			onEditDwellingAddress: params.props.onEditDwellingAddress,
			onEditDwellingDetails: params.props.onEditDwellingDetails,
			onEditDwellingLimits: params.props.onEditDwellingLimits
		};
	};

	/**
	 * Returns props for the comparative perils deductibles summary. 
	 **/
	const applyComparativePerilsDeductiblesSummaryProps = (): ComparativePerilsDeductiblesSummaryProps => {
		return {
			id: generateIdForComparativePerilsDeductiblesSummary(),
			possiblePerils: params.props.possiblePerils,
			comparisonPerilsDeductibleSummary: params.props.comparisonPerilsDeductiblesSummary,
			onEditPerilsDeductibles: params.props.onEditPerilsDeductibles,
			focusToErrorOnMount: !doesDwellingRequireReview && doPerilsAndDeductiblesRequireReview,
		};
	};

	// #endregion exported methods


	// #region private methods

	// #region id generators

	/**
	 * Generates id for the form. 
	 **/
	const generateIdForForm = (): string => {
		return params.id;
	}

	/**
	 * Generates id for the comparative dwelling summary. 
	 **/
	const generateIdForComparativeDwellingSummary = (): string => {
		return `${generateIdForForm()}-dwellingSummary`;
	}

	/**
	 * Generates id for the comparative perils deductibles summary. 
	 **/
	const generateIdForComparativePerilsDeductiblesSummary = (): string => {
		return `${generateIdForForm()}-perilsDeductibles`;
	}

	// #endregion id generators

	// #endregion private methods


	// #region variables

	const doesDwellingRequireReview = React.useMemo(() => {
		return params.props.comparisonDwellingAddressSummary.requiresReview
			|| params.props.comparisonDwellingDetailsSummary.requiresReview
			|| params.props.comparisonDwellingLimitsSummary.requiresReview
	},
		[
			params.props.comparisonDwellingAddressSummary.requiresReview,
			params.props.comparisonDwellingDetailsSummary.requiresReview,
			params.props.comparisonDwellingLimitsSummary.requiresReview,
		]
	);

	const doPerilsAndDeductiblesRequireReview: boolean = React.useMemo(() => {
		return params.props.comparisonPerilsDeductiblesSummary.requiresReview;
	}, [params.props.comparisonPerilsDeductiblesSummary]);

	// #endregion variables

	return {
		applyFormProps,
		applyComparativeDwellingSummaryProps,
		applyComparativePerilsDeductiblesSummaryProps,
	};
}

// Export parameters.
export {
	UseFormCoverageComparisonPersonalParameters,
}

// Export results.
export {
	UseFormCoverageComparisonPersonalResults,
}

export default useFormCoverageComparisonPersonal;